// A .ts module; the repo's eslint import resolver only knows js/jsx.
// eslint-disable-next-line import/no-unresolved, import/extensions
import { VoiceChannel } from '../../../myt_chat/worker/chatProxies';
import { getTabId, hasPeerOnBuild } from './device_diagnostics';
import { currentFingerprint, getLatestFingerprint } from '../../util_components/bundle_fingerprint';

// Tab-side half of the answer-tab election (worker half:
// src/myt_chat/worker/voiceCoordinator.ts). Every CMS tab keeps its own
// registered Twilio Device — Twilio rings every instance of an identity and
// that redundancy is the safety net — but only the elected "answer tab" rings,
// renders the pill and posts the OS notification.
//
// Kill switch: set localStorage.twilio_voice_coordinator = 'off' in a tab and
// reload. That tab then behaves exactly as it did before coordination (it
// rings, shows the pill and rebuilds its own device on every trigger).

export const VOICE_COORDINATOR_ENABLED = true;

const KILL_SWITCH_KEY = 'twilio_voice_coordinator';

// Ping cadence. The worker calls a visible tab frozen after 12 s without one,
// so this has to leave room for two throttled intervals. A hidden tab's timer
// is clamped by the browser to roughly one wake a minute; the worker's hidden
// thresholds are sized for that, not for this interval.
const PING_MS = 5000;

const isVisible = () => typeof document !== 'undefined' && document.visibilityState === 'visible';

// How long we wait for the worker's first state broadcast before deciding the
// coordinator is not going to answer and every tab should ring. Short enough
// that a broken worker costs at most one silent call, long enough that a
// healthy worker (which answers in milliseconds) never trips it.
const NO_STATE_GRACE_MS = 2000;

// How long the last worker state stays trustworthy. A live worker answers every
// ping with a direct state message, so this is PING_MS * 3 plus slack: three
// missed acks mean the worker (or its port) is gone and this tab must ring on
// its own. Raise it together with PING_MS, never independently.
export const STATE_STALE_MS = 3 * PING_MS + 2000;

// The hidden equivalent. A hidden tab cannot ask for an ack faster than the
// browser lets its timer run, so measuring its state against the visible
// threshold makes every healthy background tab fail open and ring — the exact
// thing the election exists to prevent. Two throttled minutes plus slack.
export const STATE_STALE_HIDDEN_MS = 150_000;

const stateStaleMs = () => (isVisible() ? STATE_STALE_MS : STATE_STALE_HIDDEN_MS);

let started = false;
let startedAt = 0;
let unsubscribe = null;
let pingTimer = null;
let latestState = null;
let latestStateAt = 0;
let lastRole = null;
let superseded = false;
let lastSuperseded = false;
let hasLiveCall = () => false;
const roleListeners = new Set();
const acceptedListeners = new Set();

const isKilled = () => {
  try {
    return window.localStorage.getItem(KILL_SWITCH_KEY) === 'off';
  } catch (e) {
    // Blocked storage cannot express the kill switch; coordination stays on.
    return false;
  }
};

const coordinatorActive = () =>
  VOICE_COORDINATOR_ENABLED && !isKilled() && VoiceChannel.isAvailable();

const send = (action, extra = {}) => {
  if (!coordinatorActive()) return;
  try {
    VoiceChannel.send({ action, tabId: getTabId(), ...extra });
  } catch (e) {
    // Nothing to do: a dead port cannot be revived from here, and the state it
    // stops refreshing ages out on its own.
  }
};

// Each deploy's content-hashed worker URL gives new tabs a SharedWorker of
// their own, and each worker elects its own answer tab. A tab on an older
// bundle steps back once a live peer runs the newest one.
export const supersededBy = (latest, own, peerOnLatest) =>
  !!latest && latest !== own && peerOnLatest;

/** `fn` reports whether this tab holds a pending or connected call. */
export const setLiveCallPredicate = (fn) => {
  hasLiveCall = typeof fn === 'function' ? fn : () => false;
};

const isSuperseded = () => {
  let next = false;
  try {
    const latest = getLatestFingerprint();
    next = supersededBy(latest, currentFingerprint, hasPeerOnBuild(latest));
  } catch (e) {
    next = false;
  }
  if (next && !superseded) {
    let live = true;
    try {
      live = !!hasLiveCall();
    } catch (e) {
      live = true;
    }
    // Never demote mid-call; the role is re-read once the call is gone.
    if (live) return false;
  }
  superseded = next;
  return superseded;
};

/**
 * True when this tab should ring, show the pill and post the OS notification.
 * Synchronous by design — it gates code paths (SDK invite handling, the relay
 * ring) that cannot await. Fails open in every case where coordination is
 * unavailable or silent, so a coordination bug can never mute every tab: an
 * unavailable worker, no state yet past the grace window, or a last state older
 * than the visibility-aware staleness window all make this tab an answer tab.
 */
export const isAnswerTab = () => {
  if (!coordinatorActive()) return true;
  if (isSuperseded()) return false;
  if (latestState && Date.now() - latestStateAt < stateStaleMs()) {
    // A fresh state that elected nobody must not mute every tab.
    if (!latestState.answerTabId) return true;
    return latestState.answerTabId === getTabId();
  }
  if (latestState) return true;
  // No state yet. Inside the grace window another tab is presumed to already
  // hold the role; past it, assume the worker is not answering at all.
  return Date.now() - startedAt >= NO_STATE_GRACE_MS;
};

export const getVoiceState = () => latestState;

export const onRoleChange = (cb) => {
  roleListeners.add(cb);
  return () => roleListeners.delete(cb);
};

export const onAccepted = (cb) => {
  acceptedListeners.add(cb);
  return () => acceptedListeners.delete(cb);
};

const notifyRole = () => {
  const role = isAnswerTab() ? 'answer' : 'follower';
  const supersededFlipped = superseded !== lastSuperseded;
  if (role === lastRole && !supersededFlipped) return;
  lastRole = role;
  lastSuperseded = superseded;
  const detail = {
    role,
    answerTabId: latestState?.answerTabId || null,
    openTabs: latestState?.tabs?.length ?? 0,
    reason: supersededFlipped ? 'superseded' : latestState?.reason || 'unknown',
    superseded,
    build: currentFingerprint || null
  };
  roleListeners.forEach((cb) => {
    try {
      cb(role === 'answer', detail);
    } catch (e) {
      // a broken subscriber must not stop the others
    }
  });
};

const onVoiceEvent = (event) => {
  if (!event || event.type !== 'voice') return;
  if (event.name === 'state') {
    latestState = event.payload || null;
    latestStateAt = Date.now();
    notifyRole();
    return;
  }
  if (event.name === 'accepted') {
    const { callSid, tabId } = event.payload || {};
    if (!callSid || tabId === getTabId()) return;
    acceptedListeners.forEach((cb) => {
      try {
        cb(callSid, tabId);
      } catch (e) {
        // a broken subscriber must not stop the others
      }
    });
  }
};

/** Idempotent. Returns a stop function. */
export const startVoiceCoordinator = () => {
  if (started) return () => {};
  started = true;
  startedAt = Date.now();

  if (!coordinatorActive()) {
    // Nothing to join; isAnswerTab() is already returning true.
    notifyRole();
    return () => {
      started = false;
    };
  }

  unsubscribe = VoiceChannel.subscribe(onVoiceEvent);
  send('hello', { visible: isVisible() });

  const onVisibilityChange = () => {
    const visible = isVisible();
    send('visibility', { visible });
    // Becoming visible tightens this tab's own staleness threshold, so the
    // state it is holding may already be stale by the new rule. Ask for a
    // fresh ack straight away and re-read the role against it.
    if (visible) {
      send('ping', { visible: true });
      notifyRole();
    }
  };
  const onPageHide = () => send('bye');
  document.addEventListener('visibilitychange', onVisibilityChange);
  window.addEventListener('pagehide', onPageHide);
  pingTimer = setInterval(() => {
    send('ping', { visible: isVisible() });
    // The worker acks every ping with a fresh state, so this is also where a
    // tab notices that no ack came back and promotes itself.
    notifyRole();
  }, PING_MS);

  // A tab that never hears back still has to settle into a role.
  setTimeout(notifyRole, NO_STATE_GRACE_MS);

  return () => {
    started = false;
    clearInterval(pingTimer);
    pingTimer = null;
    document.removeEventListener('visibilitychange', onVisibilityChange);
    window.removeEventListener('pagehide', onPageHide);
    send('bye');
    unsubscribe?.();
    unsubscribe = null;
    latestState = null;
    latestStateAt = 0;
    lastRole = null;
    superseded = false;
    lastSuperseded = false;
  };
};

export const reportInvite = (callSid) => {
  if (callSid) send('invite', { callSid });
};

export const reportAccepted = (callSid) => {
  if (callSid) send('accepted', { callSid });
};

export const reportEnded = (callSid) => {
  if (callSid) send('ended', { callSid });
};

/**
 * The browser-wide Twilio access token, fetched once by the worker and served
 * to every tab. Rejects when there is no shared worker; callers fall back to
 * their own fetch.
 */
export const getSharedVoiceToken = (force) => VoiceChannel.getToken(force === true);

export const isSharedVoiceTokenAvailable = () => coordinatorActive();

// Test seam: the module keeps process-wide state so every consumer sees one
// election, and tests need to put it back.
export const resetVoiceCoordinatorForTests = () => {
  started = false;
  startedAt = 0;
  latestState = null;
  latestStateAt = 0;
  lastRole = null;
  superseded = false;
  lastSuperseded = false;
  hasLiveCall = () => false;
  if (pingTimer) clearInterval(pingTimer);
  pingTimer = null;
  unsubscribe = null;
  roleListeners.clear();
  acceptedListeners.clear();
};
